import React, { useEffect, useState } from 'react'
import { 
    makeStyles, createStyles, Theme 
} from '@material-ui/core/styles';
import { 
    Card, CardHeader, CardContent, Grid, Select, MenuItem, TextField,
    FormControl, FormLabel, FormHelperText, Button, CardActions, CircularProgress,
    FormControlLabel, Checkbox, Paper, List, ListItem, ListItemIcon, 
    ListItemText
} from "@material-ui/core"; 
import { useForm, Controller, FormProvider } from "react-hook-form"
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from '@date-io/date-fns';
import { PersonalInfo, Candidate } from '../../../../types/CandidateTypes';
import { Edit, Clear, Save, InsertDriveFile } from "@material-ui/icons";
import { API, Storage } from "aws-amplify";
import isMobilePhone from "validator/lib/isMobilePhone";
import Dropzone from 'react-dropzone'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFileWord } from "@fortawesome/free-solid-svg-icons";
import GoogleAddressTextField from '../../../../components/GoogleAddressTextField';
import { differenceInYears } from 'date-fns'

const useStyles = makeStyles((theme: Theme) => 
    createStyles({
        header: {
            display: "flex"
        },
        divider: {
            marginBottom: 16
        },
        categoryCard: {
            display: "flex",
            padding: 16,
            height: 72
        },
        input: {
            width: "100%"
        },
        label: {
            marginBottom: 6
        },
        dropzone: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 97,
            paddingBottom: 97,
            paddingLeft: 20,
            paddingRight: 20,
            borderWidth: 2,
            borderRadius: 2,
            borderColor: "#bdbdbd",
            borderStyle: "dashed",
            backgroundColor: "#eeeeee",
            color: "#757575",
            outline: "none",
            transition: "border .24s ease-in-out"
        },
        dropzoneFile: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 51,
            paddingBottom: 51,
            paddingLeft: 20,
            paddingRight: 20,
            borderWidth: 2,
            borderRadius: 2,
            borderColor: "#bdbdbd",
            borderStyle: "dashed",
            backgroundColor: "#eeeeee",
            color: "#757575",
            outline: "none",
            transition: "border .24s ease-in-out"
        },
        disabled: {
            opacity: 0.4
        },
        editButton: {
            width: 100,
            marginTop: 8,
            marginRight: 8
        },
        actionButton: {
            marginRight: 8,
            marginBottom: 8,
            width: 100
        },
        addressOptions: {
            zIndex: 2,
            position: "absolute",
            width: "-webkit-fill-available"
        },
        field: {
            '&.MuiInputBase-root.Mui-disabled': {
                color: "#000000"
            },
            '&.MuiInputBase-input.Mui-disabled': {
                color: "#000000"
            }
        },
        checkbox: {
            '&.MuiCheckbox-colorSecondary.Mui-disabled': {
                color: "#f50057"
            }
        }
    })
);



interface Props {
    data?: PersonalInfo
    detail?: Candidate
    user?: any | null
    refreshCandidateData: () => void

}

export default function PersonalInfoCard({ data, detail, user, refreshCandidateData } : Props) {
    const classes = useStyles()
    const methods = useForm<PersonalInfo>({
        shouldUnregister: false
    });

    const { handleSubmit, control, errors, setValue, clearErrors, setError, getValues, watch } = methods

    const [readOnly, setReadOnly] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setAPIError] = useState<string>()

    const [addressInput, setAddressInput] = useState<string>('')
    const [addressValue, setAddressValue] = useState<string>()

    const [uploadedCvFile, setCvFile] = useState<Array<File>>([])
    const [uploadedProfilePhoto, setProfilePhoto] = useState<Array<File>>([])

    const onSave = (data: PersonalInfo) => {
        if(detail) {
            if(isMobilePhone(data.phone, ["en-AU", "en-NZ"])) {
                if(!addressValue) {
                    setError("address", {message: "Invalid address"})
                } else {
                    setLoading(true)
                    data.address = addressValue
                    const params = {
                        body: {
                            data: data,
                            section: "profile",
                            cvFile: getValues("cvFile")
                        }
                    };
                    API.post("", `/candidates/${detail?.username}/profile/update`,params)
                    .then(() => {
                        setReadOnly(true);
                        setLoading(false);
                        refreshCandidateData()
                    })
                    .catch((error: any) => {
                        console.log("ERROR updating candidate profile", error);
                        setLoading(false)
                        setAPIError("Error: unable to update info")
                    })   
                }
            } else {
                setError("phone", {message: "Invalid message"})
            }
        }
    }

    useEffect(() => {
        if(data) {
            setValue("title", data.title)
            setValue("firstName", data.firstName)
            setValue("surname", data.surname)
            setValue("dob", data.dob)
            setValue("address", data.address)
            if(data.address) {
                setAddressInput(data.address)
                setAddressValue(data.address)
            }
            setValue("phone", data.phone)
            setValue("email", data.email)
            setValue("gender", data.gender)
            setValue("jobType", data.jobType)
            setValue("availability", data.availability)
            setValue("streetNumber", data.streetNumber)
            setValue("street", data.street)
            setValue("suburb", data.suburb)
            setValue("region", data.region)
            setValue("country", data.country)
            setValue("postcode", data.postcode)
            setValue("profilePhoto", data.profilePhoto)
            setValue("criminalConvictions", data.criminalConvictions)
            setValue("criminalConvictionDetail", data.criminalConvictionDetail)
            setValue("transport", data.transport)

        } else if(detail) {
            setValue("title", detail.title)
            setValue("firstName", detail.firstName)
            setValue("surname", detail.surname)
            setValue("phone", detail.phone)
            setValue("email", detail.email)
            setValue("cvFile", detail.cvFile)
            setValue("jobType", detail.jobType)
        }
    }, [data, detail, setValue])

    const handleCvFileDrop = (files: any) => {
        if(user) {
            var objKey = user + "_CV_" + files[0].name;
        
            console.log("FILE UPLOAD", objKey)
        
            setLoading(true)
            Storage.put(objKey, files[0])
            .then(() => {
                Storage.get(objKey, { level: 'public', download:false })
                    .then(() => { 
                        setValue("cvFile", objKey)
                        setCvFile(files)
                        
                    })
                    .catch((error: any) => { 
                      console.log("ERROR fetching from s3", error);
                    });
            })
            .catch((error: any) => {
              console.log("ERROR uploading to s3", error);
            });
            setLoading(false)
        }
    }

    const handleProfilePhotoDrop = (files: any) => {
        if(user) {
            var objKey = user + "_PROFILEPIC_" + files[0].name;
        
            console.log("FILE UPLOAD", objKey)
        
            setLoading(true)
            Storage.put(objKey, files[0])
            .then(() => {
                Storage.get(objKey, { level: 'public', download:false })
                    .then(() => { 
                        setValue("profilePhoto", objKey)
                        setProfilePhoto(files)
                        
                    })
                    .catch((error: any) => { 
                      console.log("ERROR fetching from s3", error);
                    });
            })
            .catch((error: any) => {
              console.log("ERROR uploading to s3", error);
            });
            setLoading(false)
        }
    }

    function calcSize(fileSize: number): string {
        if (fileSize < 1000000) {
            return `${Math.round(fileSize/Math.pow(10,3))} KB`
        }
        return `${(fileSize/Math.pow(10,6)).toFixed(1)} MB`
    }

    const getCvFileDropzoneClass = () => {
        if(detail?.cvFile && readOnly) {
            return classes.dropzoneFile
        } else if (readOnly) {
            return `${classes.dropzone} ${classes.disabled}`
        } else {
            return classes.dropzone
        }
    }

    return (
        <div id="personal-info">
            <Card>
                <CardHeader title="Personal Information" action={
                    <Button variant="contained" color="primary" className={classes.editButton} onClick={() => setReadOnly(false)}>
                        Edit
                        <Edit style={{marginLeft: 4}} fontSize="small" />
                    </Button>
                }/>
                <CardContent id="personal-info-div">
                    <FormProvider {...methods}>
                        <Grid container spacing={2}>

                            <Grid item xs={12} lg={6}>
                                <FormControl
                                    className={classes.input}>

                                    {((!data?.profilePhoto && readOnly) || !readOnly) ?
                                        <>
                                            <FormLabel className={classes.label}>Upload Profile Photo below.</FormLabel>
                                            <Controller
                                                control={control}
                                                name="profilePhoto"
                                                defaultValue=""
                                                render={() => (
                                                <div>

                                                    <Dropzone onDrop={handleProfilePhotoDrop} multiple={false} disabled={readOnly}
                                                    accept={[
                                                        'image/jpeg',
                                                        'image/png']}>
                                                    {({ getRootProps, getInputProps }) => (
                                                        <Paper
                                                        variant="outlined"
                                                        {...getRootProps({className: (readOnly) ? `${classes.dropzone} ${classes.disabled}` : classes.dropzone})}
                                                        >
                                                        <input {...getInputProps()} name="profilePhoto" />
                                                        <p>Drag 'n' drop files here, or click to select files</p>
                                                        </Paper>
                                                    )}
                                                    </Dropzone>
                                                    {uploadedProfilePhoto.length !== 0 && 
                                                        <List>
                                                        {uploadedProfilePhoto.map((f: File, index: number) => (
                                                        <ListItem key={index}>
                                                            <ListItemIcon>
                                                            <InsertDriveFile />
                                                            </ListItemIcon>
                                                            <ListItemText primary={f.name} secondary={calcSize(f.size)} />
                                                        </ListItem>
                                                        ))}
                                                    </List>
                                                    }
                                                </div>
                                                )}
                                            />
                                        </>
                                    :
                                        <>
                                            <FormLabel className={classes.label}>Profile Photo</FormLabel>
                                            <a href={`https://lr-onboarding-candidate-files.s3-ap-southeast-2.amazonaws.com/public/${data?.profilePhoto}`}> 
                                                <img style={{height: 246}} src={`https://lr-onboarding-candidate-files.s3-ap-southeast-2.amazonaws.com/public/${data?.profilePhoto}`} />
                                            </a>
                                        </>
                                    }
                                    
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} lg={6}>

                                {(detail?.cvFile?.endsWith(".png") || detail?.cvFile?.endsWith(".jpeg") || detail?.cvFile?.endsWith(".jpg")) ?
                                    <FormControl style={{marginBottom: 16}}>
                                        <FormLabel className={classes.label}>CV/Resume</FormLabel>
                                        <a href={`https://lr-onboarding-candidate-files.s3-ap-southeast-2.amazonaws.com/public/${detail.cvFile}`}> 
                                            <img style={{height: 246}} src={`https://lr-onboarding-candidate-files.s3-ap-southeast-2.amazonaws.com/public/${detail?.cvFile}`} />
                                        </a>
                                    </FormControl>
                                :
                                    <FormControl
                                        className={classes.input}>

                                        {(detail?.cvFile && readOnly) ?
                                            <FormLabel className={classes.label}>CV/Resume</FormLabel>
                                        :
                                            <FormLabel className={classes.label}>Upload CV/Resume below.</FormLabel>
                                        }
                                        <Controller
                                            control={control}
                                            name="cvFile"
                                            defaultValue=""
                                            render={() => (
                                            <div >
                                                
                                                <Dropzone onDrop={handleCvFileDrop} multiple={false} disabled={readOnly}
                                                accept={[
                                                    'image/jpeg',
                                                    'image/png',
                                                    'application/pdf',
                                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}>
                                                {({ getRootProps, getInputProps }) => (
                                                    <Paper
                                                    variant="outlined"
                                                    {...getRootProps({className: getCvFileDropzoneClass()})}
                                                    >
                                                    <input {...getInputProps()} name="cvFile" />
                                                    {detail &&
                                                        <>
                                                        {(detail.cvFile && readOnly) ?
                                                            <a href={`https://lr-onboarding-candidate-files.s3-ap-southeast-2.amazonaws.com/public/${detail.cvFile}`}>
                                                                <FontAwesomeIcon style={{color: "#9e9e9e"}} icon={(detail.cvFile.endsWith(".pdf")) ? faFilePdf : faFileWord} size="10x" />
                                                            </a>
                                                        :
                                                            <p>Drag 'n' drop files here, or click to select files</p>
                                                        }
                                                        </>
                                                    }
                                                    
                                                    </Paper>
                                                )}
                                                </Dropzone>
                                                {uploadedCvFile.length !== 0 && 
                                                    <List>
                                                    {uploadedCvFile.map((f: File, index: number) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                        <InsertDriveFile />
                                                        </ListItemIcon>
                                                        <ListItemText primary={f.name} secondary={calcSize(f.size)} />
                                                    </ListItem>
                                                    ))}
                                                </List>
                                                }
                                            </div>
                                            )}
                                        />
                                    </FormControl>
                                }
                            </Grid>

                            <Grid item xs={12} lg={6} xl={4}>
                                <FormControl
                                    error={Boolean(errors.title)} 
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>Title</FormLabel>

                                    <Controller
                                        as={
                                            <Select variant="filled"
                                                defaultValue=""
                                                disabled={readOnly}>
                                                <MenuItem value="">Select a Title</MenuItem>
                                                <MenuItem value="Mr">Mr</MenuItem>
                                                <MenuItem value="Miss">Miss</MenuItem>
                                                <MenuItem value="Ms">Ms</MenuItem> 
                                                <MenuItem value="Mrs">Mrs</MenuItem>
                                            </Select>
                                        }
                                        name="title"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            required: "This is required"
                                        }}
                                    />
                                    <FormHelperText>
                                        {errors.title && errors.title.message}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} lg={6} xl={4}>

                                <FormControl
                                    error={Boolean(errors.firstName)} 
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>First Name</FormLabel>

                                    <Controller
                                        as={
                                            <TextField
                                            placeholder="Enter first name"
                                            fullWidth
                                            variant="filled"
                                            error={errors.firstName ? true : false}
                                            helperText={errors.firstName?.message}
                                            disabled={readOnly}/>
                                        }
                                        name="firstName"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                        required: "This is required"
                                        }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} lg={6} xl={4}>

                                <FormControl
                                    error={Boolean(errors.surname)} 
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>Surname</FormLabel>

                                    <Controller
                                        as={
                                            <TextField
                                                placeholder="Enter surname"
                                                fullWidth
                                                variant="filled"
                                                error={errors.surname ? true : false}
                                                helperText={errors.surname?.message}
                                                disabled={readOnly}/>
                                        }
                                        name="surname"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                        required: "This is required"
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} lg={6} xl={4}>

                                <GoogleAddressTextField 
                                    addressInput={addressInput} 
                                    setAddressInput={setAddressInput} 
                                    setAddressValue={setAddressValue} 
                                    readOnly={readOnly}
                                />
                                
                            </Grid>

                            <Grid item xs={12} lg={6} xl={4}>

                                <FormControl
                                    error={Boolean(errors.gender)}
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>Gender</FormLabel>

                                    <Controller
                                        as={
                                            <Select variant="filled" 
                                                defaultValue=""
                                                disabled={readOnly}>
                                                <MenuItem value="">Select an option</MenuItem>
                                                <MenuItem value="Male">Male</MenuItem>
                                                <MenuItem value="Female">Female</MenuItem>
                                                <MenuItem value="Other">Other</MenuItem>
                                            </Select>
                                        }
                                        name="gender"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            required: "This is required"
                                        }}
                                    />
                                    <FormHelperText>
                                        {errors.gender && errors.gender.message}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} lg={6} xl={4}>

                                <FormControl
                                    error={Boolean(errors.dob)} 
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>{`Date of Birth - ${differenceInYears( new Date(), new Date(data?.dob!))} years`}</FormLabel>

                                    <Controller
                                        render={({onChange, value}) => (
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <Grid container>
                                                    <KeyboardDatePicker
                                                        autoOk
                                                        placeholder="DD/MM/YYYY"
                                                        variant="inline"
                                                        format="dd/MM/yyyy"
                                                        margin="normal"
                                                        id="date-picker6"
                                                        value={value}
                                                        onChange={date => onChange(date)}
                                                        disabled={readOnly}/>
                                                </Grid>
                                            </MuiPickersUtilsProvider>
                                        )}
                                        name="dob"
                                        control={control}
                                        defaultValue={null}
                                        rules={{
                                        required: "This is required"
                                        }}
                                    />
                                    <FormHelperText>
                                        {errors.dob && errors.dob.message}
                                    </FormHelperText>
                                </FormControl>

                            </Grid>
                            <Grid item xs={12} lg={6} xl={4}>
                                <FormControl
                                    error={Boolean(errors.email)} 
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>Email</FormLabel>

                                    <Controller
                                        as={
                                            <TextField
                                                placeholder="Enter email"
                                                fullWidth
                                                variant="filled"
                                                error={errors.email ? true : false}
                                                helperText={errors.email?.message}
                                                disabled />
                                        }
                                        name="email"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                        required: "This is required"
                                        }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} lg={6} xl={4}>

                                <FormControl 
                                    className={classes.input}
                                    error={Boolean(errors.phone)}>

                                    <FormLabel className={classes.label}>Mobile number</FormLabel>

                                    <Controller
                                    render={({onChange, value}) => (
                                        <TextField
                                        placeholder="Enter mobile number"
                                        fullWidth
                                        variant="filled"
                                        value={value}
                                        onChange={v => {
                                            onChange(v)
                                            clearErrors("phone")
                                        }}
                                        error={errors.phone ? true : false}
                                        helperText={errors.phone?.message}
                                        disabled={readOnly}/>
                                    )}
                                    name="phone"
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                        required: "This is required"
                                    }}
                                    />
                                </FormControl>

                            </Grid>
                            <Grid item xs={12} lg={6} xl={4}>

                                <FormControl
                                    error={Boolean(errors.jobType)} 
                                    style={{width: "50%"}}>

                                    <FormLabel className={classes.label}>Select Job Type/s</FormLabel>

                                    {[{"label": "Permanent", "name": "permanent"}, {"label": "Temporary", "name": "temporary"}].map(item => (
                                        <Controller
                                            key={item.label}
                                            render={(props) => (
                                                <FormControlLabel 
                                                    control={
                                                        <Checkbox
                                                            onChange={e => {
                                                                props.onChange(e.target.checked)
                                                            }} 
                                                            checked={props.value}
                                                            disabled={readOnly} />
                                                    } 
                                                    label={item.label} />
                                            )}
                                            name={`jobType.${item.name}`}
                                            control={control}
                                            defaultValue={false}
                                            rules={{
                                                validate: (value: boolean) => {
                                                    if(value) {
                                                        clearErrors("jobType")
                                                        return true
                                                    } else {
                                                        let v = watch("jobType")
                                                        if(v) {
                                                            if(v.permanent === false && v.temporary === false) {
                                                                setError("jobType", { message: "At least 1 required"})
                                                                return false
                                                            }
                                                        } else {
                                                            setError("jobType", { message: "At least 1 required"})
                                                            return false
                                                        }
                                                        return true
                                                    }
                                                    
                                                }
                                            }}
                                        />
                                    ))}

                                    <FormHelperText>
                                        {errors.jobType && "At least 1 required"}
                                    </FormHelperText>
                                </FormControl>

                                <FormControl
                                    error={Boolean(errors.availability)}
                                    style={{width: "50%"}}>

                                    <FormLabel className={classes.label}>Availability?</FormLabel>

                                    {[{"label": "Full Time", "name": "fullTime"}, {"label": "Shift Work", "name": "shiftWork"}, {"label": "Contract", "name": "contract"}].map(item => (
                                        <Controller
                                            key={item.label}
                                            render={(props) => (
                                                <FormControlLabel 
                                                    control={
                                                        <Checkbox onChange={e => {
                                                            props.onChange(e.target.checked)
                                                        }} 
                                                        checked={props.value}
                                                        disabled={readOnly} />
                                                    } 
                                                    label={item.label} />
                                            )}
                                            name={`availability.${item.name}`}
                                            control={control}
                                            defaultValue={false}
                                            rules={{
                                                validate: (value: boolean) => {
                                                    if(value) {
                                                        clearErrors("availability")
                                                        return true
                                                    } else {
                                                        let v = getValues("availability")
                                                        if(v) {
                                                            if(v.fullTime === false && v.shiftWork === false && v.contract === false) {
                                                                setError("availability", { message: "At least 1 required"})
                                                                return false
                                                            }
                                                        } else {
                                                            setError("availability", { message: "At least 1 required"})
                                                            return false
                                                        }
                                                        return true
                                                    }
                                                }
                                            }}
                                        />
                                    ))}

                                    <FormHelperText>
                                        {errors.availability && "At least 1 required"}
                                    </FormHelperText>
                                    
                                </FormControl>

                            </Grid>
                        </Grid>
                        <FormControl
                                    error={Boolean(errors.criminalConvictions)}
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>Do you have any past / pending criminal convictions?</FormLabel>

                                    <Controller
                                        render={({ onChange, value }) => (
                                            <Select
                                                className={classes.field}
                                                variant="filled"
                                                defaultValue=""
                                                disabled={readOnly}
                                                value={value}
                                                onChange={event => {
                                                    let val = event.target.value as string
                                                    onChange(val)
                                                    if (val === "No") {
                                                        clearErrors("criminalConvictionDetail")
                                                    }
                                                }}>
                                                <MenuItem value="">Select an option</MenuItem>
                                                <MenuItem value="Yes">Yes</MenuItem>
                                                <MenuItem value="No">No</MenuItem>
                                            </Select>
                                        )}
                                        name="criminalConvictions"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            required: "This is required"
                                        }}
                                    />
                                    <FormHelperText>
                                        {errors.criminalConvictions && errors.criminalConvictions.message}
                                    </FormHelperText>
                                </FormControl>

                                {watch("criminalConvictions") === "Yes" &&
                                    <FormControl
                                        error={Boolean(errors.criminalConvictionDetail)}
                                        className={classes.input}>

                                        <FormLabel className={classes.label}>Provide details on your convictions</FormLabel>

                                        <Controller
                                            as={
                                                <TextField
                                                    inputProps={{
                                                        className: classes.field
                                                    }}
                                                    placeholder="Enter detail"
                                                    fullWidth
                                                    variant="filled"
                                                    error={errors.criminalConvictionDetail ? true : false}
                                                    helperText={errors.criminalConvictionDetail?.message}
                                                    disabled={readOnly} />
                                            }
                                            name="criminalConvictionDetail"
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                                required: "This is required"
                                            }}
                                        />
                                    </FormControl>
                                }

                                <FormControl
                                    error={Boolean(errors.transport)}
                                    className={classes.input}>

                                    <FormLabel className={classes.label}>Do you have reliable transport?</FormLabel>

                                    <Controller
                                        as={
                                            <Select
                                                className={classes.field}
                                                variant="filled"
                                                defaultValue=""
                                                disabled={readOnly}>
                                                <MenuItem value="">Select an option</MenuItem>
                                                <MenuItem value="Yes">Yes</MenuItem>
                                                <MenuItem value="No">No</MenuItem>
                                            </Select>
                                        }
                                        name="transport"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            required: "This is required"
                                        }}
                                    />
                                    <FormHelperText>
                                        {errors.transport && errors.transport.message}
                                    </FormHelperText>
                                </FormControl>
                    </FormProvider>
                </CardContent>
                <CardActions style={{display: "flex"}}>
                    <div style={{flexGrow: 1}}/>
                        {!readOnly &&
                            <div>
                                {error &&
                                    <span style={{color: "red", marginRight: 8}}>{error}</span>
                                }

                                <Button variant="contained" color="secondary" className={classes.actionButton} onClick={() => {
                                    setReadOnly(true)
                                    refreshCandidateData()
                                    clearErrors()
                                }}>
                                    Cancel
                                    <Clear style={{marginLeft: 4}} fontSize="small" />
                                </Button>
                                {loading ?
                                    <Button variant="contained" color="primary" className={classes.actionButton}>
                                        <CircularProgress size={25} style={{color: "white"}} />
                                    </Button>
                                :
                                    <Button onClick={handleSubmit(onSave)} variant="contained" color="primary" className={classes.actionButton}>
                                        Save
                                        <Save style={{marginLeft: 4}} fontSize="small" />
                                    </Button>
                                }
                            </div>
                        }
                </CardActions>
            </Card>
        </div>
    )
}